import { AuthenticationResult, EndSessionRequest, EventMessage, EventType } from '@azure/msal-browser';
import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError, filter, map, takeUntil } from 'rxjs/operators';
import { CmxHttpClient } from 'src/app/services/http-client.service';
import { IamBaseService } from './iam-base.service';
import { MsalErrorConstants } from '../constants/msal-error.constants';

@Injectable()
export class IamAuthService extends IamBaseService {
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private cmxHttp: CmxHttpClient,
    protected msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    super(msalService);
  }

  public logoutIAM(urlToRedirect?: string) {
    sessionStorage.removeItem('loginByIAM');

    let logoutRequest: EndSessionRequest = {}

    if (urlToRedirect) {
      logoutRequest.postLogoutRedirectUri = `${this.getPostLogoutRedirectUri()}'?returnUrl='${urlToRedirect}`;
    }

    const signinAccount = this.getSigninAccount();
    if (signinAccount) {
      logoutRequest.account = signinAccount;
    }

    this.msalService.logoutRedirect(logoutRequest).subscribe(result => {
      this.msalService.instance.setActiveAccount(null);
    }, error => {
      console.error('Error during logout:', error);
      this.clearSession();
    });
  }

  public handleMsalEvents() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.error('IAM Login failed:', result.error);
        sessionStorage.setItem('errorLogin', MsalErrorConstants.VIEWS_LOGIN_IAM_ERROR_SERVER);
        this.logoutIAM();
      });
  }

  public unsubscribeMsalEvents() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  public getUserProfile(): Observable<any> {
    return this.cmxHttp.get('v7/secm/users/profile?include=userinfo,profile,applications,roles,customers').pipe(
      map(data => data),
      catchError((error) => this.handleError(error))
    );
  }

  private handleError(error: any) {
    return throwError(error || 'Server error');
  }

  public async getTokenSilently(): Promise<string | null> {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      console.warn('No active account found');
      return null;
    }

    try {
      const result: AuthenticationResult = await this.msalService.instance.acquireTokenSilent({
        account: activeAccount,
        scopes: ['user.read']
      });
      return result.accessToken;
    } catch (error) {
      console.error('Error acquiring token silently:', error);
      return null;
    }
  }

  public async getAccessTokenDirect(scopes: string[] = ['user.read']): Promise<string | null> {
    const activeAccount = this.getSigninAccount();
    if (!activeAccount) {
      console.warn('No active account found for direct token access');
      return null;
    }

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        account: activeAccount,
        scopes: scopes
      });
      return result.accessToken;
    } catch (error) {
      console.error('Error acquiring token directly:', error);
      return null;
    }
  }
}
